/**
 * Descuento por pagar la colegiatura del año completa por anticipado.
 *
 * El colegio rebaja un 7% a quien paga de una vez la colegiatura de todo el año,
 * y un 10% cuando son hermanos y se paga la de todos. La rebaja es sobre la
 * COLEGIATURA; la inscripcion se paga entera.
 *
 * El reparto vive aqui, y no en el servidor, porque el dispositivo tiene que
 * llegar al MISMO numero: la caja cobra sin conexion y el servidor valida
 * despues. Si cada lado redondeara a su manera, el pago llegaria descuadrado por
 * unos centavos y acabaria en la bandeja de conflictos sin que nadie entienda
 * por que.
 */

/** Rebaja cuando paga el año completo un solo alumno. */
export const DESCUENTO_ANUAL_INDIVIDUAL = 7;

/** Rebaja cuando son hermanos y se paga el año completo de todos. */
export const DESCUENTO_ANUAL_HERMANOS = 10;

/** Los unicos porcentajes que el colegio concede hoy. */
export const PORCENTAJES_DE_DESCUENTO_ANUAL: readonly number[] = Object.freeze([
  DESCUENTO_ANUAL_INDIVIDUAL,
  DESCUENTO_ANUAL_HERMANOS,
]);

/**
 * Cuanto se le rebaja a cada mensualidad, en centavos.
 *
 * Se calcula mes a mes y no sobre el total repartido despues, para que una
 * mensualidad valga siempre lo mismo tenga el alumno diez meses o doce, y para
 * que el recibo pueda explicar cada linea por separado.
 *
 * El redondeo sobrante va al ULTIMO mes. Con los precios del colegio no sobra
 * nada —el 7% de Q450 son Q31.50 exactos—, pero un precio que no sea multiplo
 * de 100 dejaria centavos sueltos, y prefiero que caigan en un sitio decidido
 * antes que en el que toque por azar.
 */
export function repartirDescuentoAnual(
  importesMinor: readonly number[],
  porcentaje: number,
): number[] {
  if (!Number.isFinite(porcentaje) || porcentaje <= 0 || porcentaje >= 100) {
    throw new RangeError(`Porcentaje de descuento inválido: ${porcentaje}`);
  }
  if (importesMinor.length === 0) return [];

  const total = importesMinor.reduce((suma, importe) => suma + importe, 0);
  const objetivo = Math.round((total * porcentaje) / 100);

  const reparto = importesMinor.map((importe) =>
    Math.round((importe * porcentaje) / 100),
  );
  const repartido = reparto.reduce((suma, parte) => suma + parte, 0);
  const sobra = objetivo - repartido;
  if (sobra !== 0) {
    const ultimo = reparto.length - 1;
    // Nunca se le rebaja a un mes mas de lo que vale: con el ajuste pegado al
    // ultimo mes, un descuento del 99% podria pasarse.
    reparto[ultimo] = Math.min(
      importesMinor[ultimo] ?? 0,
      Math.max(0, (reparto[ultimo] ?? 0) + sobra),
    );
  }
  return reparto;
}

/** Total que se rebaja: la suma del reparto, sin recalcularlo por otro camino. */
export function totalDescuentoAnual(
  importesMinor: readonly number[],
  porcentaje: number,
): number {
  return repartirDescuentoAnual(importesMinor, porcentaje).reduce(
    (suma, parte) => suma + parte,
    0,
  );
}

/** Texto que se imprime en el recibo y queda guardado como motivo. */
export function motivoDescuentoAnual(porcentaje: number): string {
  return porcentaje === DESCUENTO_ANUAL_HERMANOS
    ? `Descuento ${porcentaje}% por pago anual de hermanos`
    : `Descuento ${porcentaje}% por pago anual anticipado`;
}
